/**
 * FlowSurface - React Flow implementation for visualizing note connections
 */
import { uiLogger } from '@annote/core/utils/debug';
import React, { useCallback, useEffect, useRef, useContext, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useBrainContext } from 'src/context/BrainContext';
import { useSuggestedContext } from 'src/context/SuggestedContext';
import { annoteIndex } from 'src/context/annoteIndex';
import { useNotes } from 'src/context/noteCacheHooks';
import { ShownIdsContext } from 'src/context/ShownContext';
import { useDrop } from 'react-dnd';
import { Note, isAnnote, UUID } from '@annote/types';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Panel,
  type Node,
  type OnConnectStartParams,
  Edge,
  Connection,
  OnEdgesDelete,
  useReactFlow,
  OnConnectEnd,
  NodeOrigin,
  SelectionMode,
  ReactFlowProvider,
  useViewport,
  OnNodesDelete,
} from '@xyflow/react';
import { BrushCleaning, CreditCard, LayoutGrid, Minus, Plus, Scan } from 'lucide-react';
import { colors, dimensions } from '@annote/core/styles';
import { useDragging } from 'src/context/DraggingContext';
import { FocusContext } from 'src/context/FocusContext';
import { useDndRef } from 'src/helpers/useDndRef';
import { usePanelContext } from 'src/context/PanelContext';
import { FlowCard } from './_components/FlowCard';
import '@xyflow/react/dist/style.css';
import { edgeTypes } from './_components/Edge';

export type ArrangeFunction = (
  nodes: Node[],
  edges: Edge[],
  fixedNodeIds?: string[],
) => { nodes: Node[]; edges: Edge[] };

export interface Link {
  from: string;
  to: string;
  annoteFrom?: string; // the true fromId if an annote
}

export type NodeData = {
  note: Note;
  onResize: (id: string, width: number, height: number) => void;
};

const nodeOrigin: NodeOrigin = [0.5, 0];

const nodeTypes = {
  card: FlowCard,
};

// 1 - left mouse button
// 2 - right mouse button
const panOnDrag = [1, 2];

const Surface = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background-size: 10px 10px;
  background-position: 0 0;
  overflow: hidden;

  & .react-flow__edge-path {
    stroke: ${colors.lines.flowLink} !important;
    stroke-width: 1.5;
  }

  & .selected .react-flow__edge-path {
    stroke: ${colors.lines.selected} !important;
  }
`;

// TODO - set this programatically based on the actual node
const DEFAULT_NODE_WIDTH = 35 * 8;
const DEFAULT_NODE_HEIGHT = 168;

// Helper: normalize node size from different shapes (top-level or data)
const getNodeSize = (n: Node): { width: number; height: number } => {
  const data = n.data as { width?: number; height?: number } | undefined;
  let width: number;
  if (typeof n.width === 'number') {
    width = n.width;
  } else if (data && typeof data.width === 'number') {
    width = data.width;
  } else {
    width = DEFAULT_NODE_WIDTH;
  }

  let height: number;
  if (typeof n.height === 'number') {
    height = n.height;
  } else if (data && typeof data.height === 'number') {
    height = data.height;
  } else {
    height = DEFAULT_NODE_HEIGHT;
  }

  return { width, height };
};

// Create a new context

// Add this with your other type definitions
type CustomEdgeData = {
  annoteFrom?: string;
};

// Update the Edge type to include the data property
type CustomEdge = Edge<CustomEdgeData>;

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 2px;
  background: ${colors.bg.primary};
  border-radius: 8px;
  box-shadow: 0px 3px 6px 0px rgba(15, 15, 15, 0.1);
  overflow: hidden;
`;

const ToolbarButton = styled.button<{ $size?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size || 32}px;
  height: ${({ $size }) => $size || 32}px;
  padding: 0;
  margin: 0;
  background: ${colors.bg.primary};
  border: none;
  border-radius: 0;
  cursor: pointer;
  color: ${colors.icon.primary};
  transition: background 150ms ease;
  flex-shrink: 0;

  &:hover {
    background: ${colors.button.hover};
    border-radius: 4px;
  }

  svg {
    display: block;
    width: 16px;
    height: 16px;
  }
`;

const ToolbarDivider = styled.div`
  width: 16px;
  height: 1px;
  background: ${colors.borders.input.primary};
  border-radius: 2px;
  flex-shrink: 0;
`;

const FlowSurfaceContent: React.FC<{ arrangeFunction: ArrangeFunction }> = ({ arrangeFunction }) => {
  const { shownIds } = useContext(ShownIdsContext);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<CustomEdge>([]);
  const { linkNotes, unlinkNotes, updateNotes, createTakeaway, getCachedNote } = useBrainContext();
  const { suggestedLinks, rejectAllSuggestions } = useSuggestedContext();
  const { showAafterB, showAbeforeB, hide, show, clear } = useContext(ShownIdsContext);
  const { screenToFlowPosition } = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { setDraggingConnectionId, setDraggingConnectionType, setIsOverLibrary } = useDragging();
  const reactFlowInstance = useReactFlow();
  const { x: viewportX, zoom } = useViewport();
  const { setDraggingNoteKey } = useDragging();
  const { focusedNoteId } = useContext(FocusContext);
  const { leftPanelsWidth, rightPanelsWidth, viewport } = usePanelContext();

  // These are nodes who's positions should not be changed by auto-arrange
  const [fixedNodeIds, setFixedNodeIds] = useState<string[]>([]);

  // Track which nodes have been manually moved by the user
  const [manuallyMovedNodeIds, setManuallyMovedNodeIds] = useState<Set<string>>(new Set());

  // Track nodes that are pending initial measurement
  const [pendingMeasurementIds, setPendingMeasurementIds] = useState<Set<string>>(new Set());

  // Track when all measurements are complete to trigger arrange
  const prevPendingCountRef = useRef<number>(0);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  const shownAnnoteIds = useMemo(() => annoteIndex.getAnnoteIds(shownIds), [shownIds]);
  const shownAnnotes = useNotes(shownAnnoteIds);
  // Also load the notes that are explicitly shown (non-annote notes)
  const shownNotesFromIds = useNotes(shownIds);

  // Ensure all shown Annotes also have their source shown.
  useEffect(() => {
    const neededSourceIds = Array.from(
      new Set(
        shownAnnotes
          .filter((n): n is Note => !!n && isAnnote(n))
          .map((annote) => annote.createdFromId)
          .filter((id) => !!id && !shownIds.includes(id)),
      ),
    );

    if (neededSourceIds.length) show(neededSourceIds as UUID[]);
  }, [shownAnnotes]); // should not need to depend on shownIds, show

  // Accept dropped cards from the library
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [{ isOver }, dropRef] = useDrop<Note, void, { isOver: boolean }>({
    accept: ['TAKEAWAY', 'SOURCE', 'ANNOTE', 'CARD', 'LISTCARD'],
    drop: (item, monitor) => {
      if (monitor.didDrop()) return; // the item was dropped in a nested spot
      show(item.id);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop(),
    }),
  });

  // Use the hook instead of creating a callback manually
  const setDropRef = useDndRef(dropRef);

  useEffect(() => {
    // This is a workaround to get the latest nodes in handleAutowithout causing an infinite re-render loop
    // console.log('nodes updated', nodes);
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    // This is a workaround to get the latest nodes in handleAutowithout causing an infinite re-render loop
    edgesRef.current = edges;
  }, [edges]);

  /**
   * Calculate panel-aware padding for fitView
   * Uses viewport from PanelContext and applies 10% padding to the usable area
   */
  const calculateFitViewPadding = useCallback(() => {
    const basePadding = 0.1; // 10% base padding for comfort
    const flowBounds = reactFlowWrapper.current?.getBoundingClientRect();

    if (!flowBounds) {
      return basePadding;
    }

    // Convert viewport dimensions to ReactFlow padding fractions
    // ReactFlow padding is relative to the full flow bounds
    const padding = {
      top: basePadding,
      right: basePadding + rightPanelsWidth / flowBounds.width,
      bottom: basePadding,
      left: basePadding + leftPanelsWidth / flowBounds.width,
    };

    uiLogger.debug('calculateFitViewPadding:', { viewport, padding });

    return padding;
  }, [viewport, leftPanelsWidth, rightPanelsWidth]);

  /**
   * Centralized fitView that accounts for panel overlays
   * Ensures all cards zoom out and center to fit within viewport width
   */
  const fitViewWithPanels = useCallback(
    (focusNodeIds?: string[], duration = 300) => {
      const padding = calculateFitViewPadding();

      const fitViewOptions = {
        padding,
        duration,
        minZoom: 0.1, // Allow zooming out significantly to fit all nodes
        maxZoom: 1.5, // Don't zoom in too much
        includeHiddenNodes: false, // Only fit visible nodes
      };

      // Robust fit: compute nodes bounding box in flow coords and set viewport
      const allNodes = (reactFlowInstance.getNodes ? reactFlowInstance.getNodes() : nodesRef.current) || [];

      const targetNodes =
        focusNodeIds && focusNodeIds.length > 0 ? allNodes.filter((n) => focusNodeIds.includes(n.id)) : allNodes;

      if (targetNodes.length === 0) {
        // fallback to fitView if no nodes
        if (typeof reactFlowInstance.fitView === 'function') {
          reactFlowInstance.fitView({ padding, duration });
        }
        return;
      }

      // compute bounding box (node.position is top-left)
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      targetNodes.forEach((n) => {
        const nx = typeof n.position === 'object' ? n.position.x ?? 0 : 0;
        const ny = typeof n.position === 'object' ? n.position.y ?? 0 : 0;
        const { width: w, height: h } = getNodeSize(n);
        minX = Math.min(minX, nx);
        minY = Math.min(minY, ny);
        maxX = Math.max(maxX, nx + w);
        maxY = Math.max(maxY, ny + h);
      });

      const bboxWidth = Math.max(1, maxX - minX);
      const bboxHeight = Math.max(1, maxY - minY);

      // compute desired zoom so bbox fits in viewport (pixel math)
      const vw = Math.max(1, viewport.width);
      const vh = Math.max(1, viewport.height);
      const zoomX = vw / bboxWidth;
      const zoomY = vh / bboxHeight;
      let calculatedZoom = Math.min(zoomX, zoomY);
      // apply padding fraction from calculateFitViewPadding (use basePadding)
      const basePadding = 0.1;
      calculatedZoom *= 1 - basePadding * 2;

      // clamp
      const minZoom = fitViewOptions.minZoom || 0.1;
      const maxZoom = fitViewOptions.maxZoom || 1.5;
      calculatedZoom = Math.max(minZoom, Math.min(maxZoom, calculatedZoom));

      // compute center of bbox in flow coords
      const centerX = minX + bboxWidth / 2;
      const centerY = minY + bboxHeight / 2;

      // compute x,y pan so center maps to center of viewport
      // Use panel-adjusted viewport center (viewport.x includes leftPanelsWidth already)
      const viewportCenterX = viewport.x + viewport.width / 2;
      const viewportCenterY = viewport.y + viewport.height / 2;
      const panX = viewportCenterX - centerX * calculatedZoom + 40; // Shift right as its offset oddly to the left
      const panY = viewportCenterY - centerY * calculatedZoom;

      type MaybeViewport = {
        setViewport?: (v: { x: number; y: number; zoom: number }, opts?: { duration: number }) => void;
      };
      const setViewportFn = (reactFlowInstance as unknown as MaybeViewport).setViewport;
      if (typeof setViewportFn === 'function') {
        try {
          setViewportFn({ x: panX, y: panY, zoom: calculatedZoom }, { duration });
        } catch (e) {
          setViewportFn({ x: panX, y: panY, zoom: calculatedZoom });
        }
      } else if (typeof reactFlowInstance.fitView === 'function') {
        reactFlowInstance.fitView({ padding: fitViewOptions.padding, duration: fitViewOptions.duration });
      }
    },
    [reactFlowInstance, calculateFitViewPadding],
  );

  const handleAutoArrange = useCallback(
    (clearManualPositions = false) => {
      // If explicitly called (arrange button), clear manual positions and do full rearrange
      if (clearManualPositions) {
        setManuallyMovedNodeIds(new Set());
      }

      // Use only fixedNodeIds (not manually moved) for explicit arrange, or include manual for auto-arrange
      const allFixedNodeIds = clearManualPositions ? fixedNodeIds : [...fixedNodeIds, ...manuallyMovedNodeIds];
      const { nodes: arrangedNodes, edges: arrangedEdges } = arrangeFunction(
        nodesRef.current,
        edgesRef.current,
        allFixedNodeIds,
      );
      uiLogger.debug('handleAutoArrange', {
        arrangedNodesCount: arrangedNodes.length,
        arrangedEdgesCount: arrangedEdges.length,
        clearManualPositions,
      });

      setNodes(arrangedNodes);
      setEdges(arrangedEdges);

      // Use requestAnimationFrame for smooth panel-aware fitView without blip
      requestAnimationFrame(() => {
        fitViewWithPanels(undefined, 300);
      });
    },
    [arrangeFunction, setNodes, setEdges, fixedNodeIds, manuallyMovedNodeIds, fitViewWithPanels],
  );

  // Trigger auto-arrange when all pending measurements complete
  useEffect(() => {
    const currentCount = pendingMeasurementIds.size;
    const prevCount = prevPendingCountRef.current;

    uiLogger.debug('pendingMeasurementIds changed', {
      prevCount,
      currentCount,
      pendingIds: Array.from(pendingMeasurementIds),
    });

    // If we had pending measurements and now have none, all nodes are measured
    if (prevCount > 0 && currentCount === 0) {
      uiLogger.debug('All nodes measured, triggering auto-arrange');
      handleAutoArrange(false);
    }

    prevPendingCountRef.current = currentCount;
  }, [pendingMeasurementIds, handleAutoArrange]);

  /**
   * @deprecated Use fitViewWithPanels instead
   */
  const refitView = useCallback(
    (focusNodeIds?: string[]) => {
      setTimeout(() => {
        fitViewWithPanels(focusNodeIds, 200);
      }, 100);
    },
    [fitViewWithPanels],
  );

  // When a node resizes here is how we handle
  // TODO we should probably use node.measured.height and width?  it's built in.
  const handleNodeResize = useCallback(
    (id: string, width: number, height: number) => {
      setNodes((prevNodes) => prevNodes.map((node) => (node.id === id ? { ...node, width, height } : node)));

      // Remove from pending measurements using functional setState to avoid stale closure
      setPendingMeasurementIds((prev) => {
        // Only update if this node was actually pending
        if (prev.has(id)) {
          uiLogger.debug('node measured, removing from pending', {
            nodeId: id,
            width,
            height,
            remainingPending: prev.size - 1,
          });
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        }
        return prev;
      });

      // if the width is > 700 focus the view on that node
      if (width > 700) {
        refitView([id]);
      }
    },
    [setNodes, refitView],
  );

  const handleClearAll = useCallback(async (): Promise<void> => {
    await rejectAllSuggestions(); // Uses default 'dismissed'
    clear();
  }, [rejectAllSuggestions, clear]);

  const onPaneDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      // Prevent the default behavior
      event.preventDefault();
      // Get the position of the double click in flow coordinates
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      // Create a new note
      const newNote = createTakeaway();

      // Create a new node with default dimensions
      const newNode: Node = {
        id: newNote.id,
        type: 'card',
        position,
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
        data: { note: newNote, onResize: handleNodeResize },
      };

      // Mark as pending measurement
      setPendingMeasurementIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(newNote.id);
        return newSet;
      });

      // keep this node's position fixed
      setFixedNodeIds((nids) => [...nids, newNote.id]);
      // Add the new node to the existing nodes
      setNodes((nds) => nds.concat(newNode));
      updateNotes([newNote]);
      show(newNote.id);

      // remove the node from the fixedNodeIds after a short delay
      setTimeout(() => {
        setFixedNodeIds((nids) => nids.filter((id) => id !== newNote.id));
      }, 10000);
    },
    [screenToFlowPosition, setNodes, createTakeaway, updateNotes, show, handleNodeResize],
  );

  // Connecting two notes
  const onConnect = useCallback(
    (params: Connection) => {
      const { source: fromId, target: toId } = params;
      if (!fromId || !toId) return;
      const fromNote = getCachedNote(fromId as UUID);
      const toNote = getCachedNote(toId as UUID);
      if (fromNote && toNote) {
        linkNotes(fromNote, toNote);
      }
    },
    [linkNotes, getCachedNote],
  );

  // Create a new note and link it if an edge is dropped on an empty space
  const onConnectEnd: OnConnectEnd = useCallback(
    async (event: MouseEvent | TouchEvent, connectionState) => {
      setDraggingConnectionId(null);
      setDraggingConnectionType(null);
      // when a connection is dropped on the pane it's not valid
      if (!connectionState.isValid) {
        // we need to remove the wrapper bounds, in order to get the correct position
        const newNote = createTakeaway();
        const fromId = connectionState?.fromNode?.id;
        if (!fromId) return;
        const { clientX, clientY } = 'changedTouches' in event ? event.changedTouches[0] : event;

        const newNode: Node = {
          id: newNote.id,
          type: 'card',
          position: screenToFlowPosition({
            x: clientX,
            y: clientY,
          }),
          width: DEFAULT_NODE_WIDTH,
          height: DEFAULT_NODE_HEIGHT,
          data: { note: newNote, onResize: handleNodeResize },
          origin: [0.5, 0.0],
        };

        // Mark as pending measurement
        setPendingMeasurementIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(newNote.id);
          return newSet;
        });

        setNodes((nds) => nds.concat(newNode));
        const fromNote = getCachedNote(fromId as UUID);
        if (!fromNote) return;
        if (connectionState.fromHandle?.type === 'source') {
          await linkNotes(fromNote, newNote);
          await showAbeforeB(newNote.id, fromId);
        } else {
          await linkNotes(newNote, fromNote);
          await showAafterB(newNote.id, fromId);
        }
      }
    },
    [
      screenToFlowPosition,
      linkNotes,
      getCachedNote,
      showAafterB,
      showAbeforeB,
      setNodes,
      createTakeaway,
      handleNodeResize,
    ],
  );

  const onEdgesDelete: OnEdgesDelete = useCallback(
    (edgesToDelete) => {
      edgesToDelete.forEach((edge) => {
        // If the linking note is an annote, we need to use it's fromId
        const fromId = edge.data?.annoteFrom || edge.source;
        const toId = edge.target;
        const fromNote = getCachedNote(fromId as UUID);
        const toNote = getCachedNote(toId as UUID);
        if (fromNote && toNote) {
          unlinkNotes(fromNote, toNote);
        }
      });
    },
    [unlinkNotes, getCachedNote],
  );

  // Sync the Nodes with shownIds
  useEffect(() => {
    // Debug: log current state when syncing nodes
    uiLogger.debug('syncing nodes start', {
      shownIds,
      shownNotesFromIdsCount: shownNotesFromIds.length,
      nodesCount: nodes.length,
    });

    // Use notes loaded directly for the shownIds to ensure the effect runs
    // when actual shown notes change.
    const shownNotes = shownNotesFromIds.filter((note) => note && note.type !== 'annote') as Note[];

    // first populate the new Node list with existing nodes
    const newNodes = nodes
      .filter((node) => shownIds.includes(node.id))
      .map((node) => {
        const noteForNode = getCachedNote(node.id as UUID);
        return {
          ...node,
          data: {
            ...node.data,
            // ensure the note and onResize are the latest
            note: noteForNode || undefined,
            onResize: handleNodeResize,
          } as NodeData,
        } as Node;
      });

    // Clean up pendingMeasurementIds for nodes that are no longer shown
    const removedNodeIds = nodes.filter((node) => !shownIds.includes(node.id)).map((node) => node.id);
    if (removedNodeIds.length > 0) {
      setPendingMeasurementIds((prev) => {
        const newSet = new Set(prev);
        removedNodeIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }

    // Track if new nodes were added to trigger arrange
    const nodeIds = new Set(newNodes.map((node) => node.id));
    const notesWithoutNodes = shownNotes.filter((n) => n.type !== 'annote' && !nodeIds.has(n.id));
    const hasNewNodes = notesWithoutNodes.length > 0;

    // Mark new nodes as pending measurement
    if (hasNewNodes) {
      setPendingMeasurementIds((prev) => {
        const newSet = new Set(prev);
        notesWithoutNodes.forEach((note) => newSet.add(note.id));
        return newSet;
      });
    }

    notesWithoutNodes.forEach((note) => {
      newNodes.push({
        id: note.id,
        type: 'card',
        position: { x: 300, y: 300 }, // Initial position, will be updated later
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
        data: { note, onResize: handleNodeResize } as NodeData,
      } as Node);
    });

    const allNodeIds = new Set(newNodes.map((node) => node.id));
    const shownLinks = newNodes.reduce<Link[]>((acc, node) => {
      const note = getCachedNote(node.id as UUID);
      if (!note) return acc;
      const linksToShownNodes = annoteIndex.expandSkipOvers(note.links, 'links').filter((id) => allNodeIds.has(id));
      linksToShownNodes.forEach((linkId) => {
        acc.push({ from: node.id, to: linkId });
      });
      // don't show links to yourself
      return acc.filter((link) => link.from !== link.to);
    }, []);

    // Create regular edges from existing links
    const regularEdges: Edge[] = shownLinks.map((link) => ({
      id: `link-${link.from}-${link.to}`,
      source: link.from,
      target: link.to,
      type: 'custom',
      curvature: -0.5,
      data: { onDelete: onEdgesDelete, annoteFrom: link.annoteFrom },
    }));

    // Create suggested edges from suggested links
    const suggestedEdges: Edge[] = suggestedLinks
      .filter((link) => allNodeIds.has(link.fromId) && allNodeIds.has(link.toId))
      .map((link) => ({
        id: link.suggestionId,
        source: link.fromId,
        target: link.toId,
        type: 'suggested',
        data: {
          reason: link.reason,
          score: link.score,
        },
      }));

    const newEdges = [...regularEdges, ...suggestedEdges];

    uiLogger.debug('syncing nodes result', {
      newNodesCount: newNodes.length,
      newEdgesCount: newEdges.length,
      hasNewNodes,
      notesWithoutNodesCount: notesWithoutNodes.length,
    });

    // Just update the nodes and edges
    // If there are new nodes, they will be measured by ResizeObserver and then arranged
    // via handleNodeResize once all measurements are complete
    setNodes(newNodes);
    setEdges(newEdges);
  }, [
    shownIds,
    shownNotesFromIds, // Re-run when notes are loaded from cache
    getCachedNote,
    suggestedLinks,
    setNodes,
    setEdges,
    handleNodeResize,
    onEdgesDelete,
    arrangeFunction,
    fixedNodeIds,
    manuallyMovedNodeIds,
  ]);
  const onNodeDragStart = useCallback((event: React.MouseEvent, node: Node) => {
    setDraggingNoteKey(`card-${node.id}`); // dragging lib only takes one right now.
    setIsOverLibrary(true); // we're just turning on the hover state for the library for any dragging now
  }, []);

  // const onNodeDrag = useCallback((event: React.MouseEvent, node: Node, nodes: Node[]) => {}, []);
  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node, draggedNodes: Node[]) => {
      setDraggingNoteKey(null);
      setIsOverLibrary(false);

      // Track that these nodes have been manually moved by the user
      setManuallyMovedNodeIds((prev) => {
        const newSet = new Set(prev);
        draggedNodes.forEach((n) => newSet.add(n.id));
        return newSet;
      });

      // if the node is dragged out of the viewport, set isOverLibrary to truec
      const flowBounds = reactFlowInstance.getViewport();

      const isOutsideLeft = (n: Node): boolean => {
        const nodeLeft = n.position.x * zoom + viewportX;
        return nodeLeft < flowBounds.x;
      };

      if (draggedNodes.some(isOutsideLeft)) {
        draggedNodes.forEach((n) => hide(n.id));
      }
    },
    [zoom, viewportX, reactFlowInstance, hide],
  );
  useEffect(() => {
    if (!focusedNoteId) return;

    const node = reactFlowInstance.getNode(focusedNoteId);
    if (!node) return;

    const { x, y, zoom: viewportZoom } = reactFlowInstance.getViewport();
    const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();

    if (!reactFlowBounds) return;

    const isNodeInView = (nodeToCheck: Node): boolean => {
      const nodeLeft = nodeToCheck.position.x * viewportZoom + x;
      const nodeTop = nodeToCheck.position.y * viewportZoom + y;
      const nodeRight = nodeLeft + (nodeToCheck.width || 0) * viewportZoom;
      const nodeBottom = nodeTop + (nodeToCheck.height || 0) * viewportZoom;

      return (
        nodeLeft >= 0 && nodeTop >= 0 && nodeRight <= reactFlowBounds.width && nodeBottom <= reactFlowBounds.height
      );
    };

    if (!isNodeInView(node)) {
      refitView([focusedNoteId]);
    }
  }, [focusedNoteId, refitView, reactFlowInstance]);

  useEffect(() => {
    uiLogger.debug('react flow instance updated', reactFlowInstance);
  }, [reactFlowInstance]);

  // Adjust React Flow viewport when aggregate panel widths change (fully reactive!)
  // No more timeouts - the context provides the correct values immediately
  // Re-center cards when viewport changes (panel resize, window resize, etc.)
  // Zooms out and centers to keep all cards within viewport width
  useEffect(() => {
    if (!reactFlowInstance) return undefined;

    uiLogger.debug('Viewport changed:', {
      viewport,
      leftPanelsWidth,
      rightPanelsWidth,
    });

    // Small delay for panel animations (250ms transition), shorter for window resize
    const timer = setTimeout(() => {
      uiLogger.debug('Executing fitViewWithPanels to zoom/center cards');
      fitViewWithPanels(undefined, 300);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [
    fitViewWithPanels,
    reactFlowInstance,
    viewport, // Responds to window resize and panel changes
    leftPanelsWidth,
    rightPanelsWidth,
  ]);

  const toolbarStyle = useMemo((): React.CSSProperties => {
    const base: React.CSSProperties = { bottom: '12px', margin: 0 };
    if (rightPanelsWidth === 0) {
      return { ...base, right: '12px' };
    }
    const rightOffsetRem = rightPanelsWidth / dimensions.rem2px + 0.75;
    return { ...base, right: `${rightOffsetRem}rem` };
  }, [rightPanelsWidth]);

  const newNote = useCallback(async (): Promise<void> => {
    const createdNote = await createTakeaway();
    show(createdNote.id);
  }, [createTakeaway, show]);

  const onNodesDelete: OnNodesDelete = useCallback(
    (nodesToDelete) => {
      hide(nodesToDelete.map((node) => node.id));
      // Prevent the default deletion behavior
      return false;
    },
    [hide],
  );

  // Workaround for double click to add note
  const lastClickTime = useRef<number>(0);
  const doubleClickDelay = 300; // milliseconds

  const handlePaneClick = useCallback(
    (event: React.MouseEvent) => {
      const currentTime = new Date().getTime();
      const timeSinceLastClick = currentTime - lastClickTime.current;
      if (timeSinceLastClick < doubleClickDelay) {
        onPaneDoubleClick(event);
      }

      lastClickTime.current = currentTime;
    },
    [onPaneDoubleClick],
  );

  /**
   * DUAL VIEWPORT SYSTEM EXPLANATION:
   *
   * 1. REACTFLOW SURFACE VIEWPORT (full page):
   *    - The ReactFlow surface is intentionally full-page width/height (100vh)
   *    - This puts the scrollbars on the edges of the page where they feel natural
   *    - Users expect scrollbars to be at the browser edge, not inside a panel
   *
   * 2. PANEL-ADJUSTED VISIBLE VIEWPORT (calculated below):
   *    - Panels (lib, reader, writer) slide in from the sides and overlay the surface
   *    - Cards should be positioned/sized to be visible in the non-covered area
   *    - This "debugViewportBounds" shows the actual visible area for card placement
   *    - fitView and auto-arrange use this to keep cards in the viewable region
   *
   * The red debug box visualizes this second, panel-adjusted viewport.
   */

  return (
    <div ref={setDropRef}>
      <Surface id="surface" ref={reactFlowWrapper}>
        <ReactFlow
          // defaultViewport={{ x: 100, y: 0, zoom: 0.5 }} // Adjust the zoom value as needed
          nodes={nodes}
          edges={edges}
          minZoom={0.05}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onConnectStart={(event: MouseEvent | TouchEvent, params: OnConnectStartParams) => {
            setDraggingConnectionId(params.nodeId);
            setDraggingConnectionType(params.handleType);
          }}
          onEdgesDelete={onEdgesDelete}
          onInit={() => {
            // Use panel-aware fitView on initialization
            setTimeout(() => {
              fitViewWithPanels(undefined, 0);
            }, 100);
          }}
          onConnectEnd={onConnectEnd}
          nodeOrigin={nodeOrigin}
          // translateExtent={translateExtent}
          // panning options
          panOnScroll={true}
          selectionOnDrag={true}
          panOnDrag={panOnDrag}
          selectionMode={SelectionMode.Partial}
          onPaneClick={handlePaneClick}
          proOptions={{ hideAttribution: true }}
          onNodeDragStart={onNodeDragStart}
          // onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          selectNodesOnDrag={false}
          onNodesDelete={onNodesDelete}
          zoomOnDoubleClick={false}
          edgeTypes={edgeTypes}
        >
          <Panel position="bottom-right" style={toolbarStyle}>
            <ToolbarContainer>
              <ToolbarButton onClick={newNote} title="Add new note" $size={28}>
                <CreditCard size={16} />
              </ToolbarButton>
              <ToolbarDivider />
              <ToolbarButton onClick={() => reactFlowInstance.zoomIn()} title="Zoom in" $size={28}>
                <Plus size={16} />
              </ToolbarButton>
              <ToolbarButton onClick={() => reactFlowInstance.zoomOut()} title="Zoom out" $size={28}>
                <Minus size={16} />
              </ToolbarButton>
              <ToolbarButton onClick={() => fitViewWithPanels()} title="Fit view" $size={28}>
                <Scan size={16} />
              </ToolbarButton>
              <ToolbarButton onClick={() => handleAutoArrange(true)} title="Reorganize" $size={28}>
                <LayoutGrid size={16} />
              </ToolbarButton>
              <ToolbarDivider />
              <ToolbarButton onClick={handleClearAll} title="Clear all notes from surface" $size={28}>
                <BrushCleaning size={16} />
              </ToolbarButton>
            </ToolbarContainer>
          </Panel>
        </ReactFlow>
      </Surface>
    </div>
  );
};

const FlowSurface: React.FC<{ arrangeFunction: ArrangeFunction }> = ({ arrangeFunction }) => {
  return (
    <ReactFlowProvider>
      <FlowSurfaceContent arrangeFunction={arrangeFunction} />
    </ReactFlowProvider>
  );
};

export default FlowSurface;
